import { useState, useEffect } from 'react';
import { Globe, AlertTriangle, RefreshCw, Check, Copy } from 'lucide-react';
import api from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';

interface AddressSpaceData {
  id: string;
  tenantId: string;
  baseCidr: string;
  connectorOctet: number;
  ikev2Octet: number;
  peeringOctet: number;
  autoAllocated: boolean;
  isLegacy: boolean;
  subnets: {
    wg_vpn: string;
    ikev2: string;
    connector: string;
    peering: string;
  };
}

interface TenantAddressSpaceProps {
  tenantId: string;
}

const CopyField = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <div className="flex items-center space-x-2">
        <code className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-0.5 rounded">{value}</code>
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="text-gray-400 hover:text-primary-600"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
};

const TenantAddressSpace = ({ tenantId }: TenantAddressSpaceProps) => {
  const [data, setData] = useState<AddressSpaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editCidr, setEditCidr] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/tenants/${tenantId}/address-space`) as any;
      setData(res.data);
      setEditCidr(res.data.baseCidr);
    } catch (err: any) {
      setError(err.message || 'Failed to load address space');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tenantId]);

  const handleAutoAllocate = async () => {
    setSaving(true);
    try {
      await api.post(`/tenants/${tenantId}/address-space/auto-allocate`);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  const [showCascadeConfirm, setShowCascadeConfirm] = useState(false);
  const [cascadeResult, setCascadeResult] = useState<any>(null);

  const handleCascade = async () => {
    setSaving(true);
    setCascadeResult(null);
    try {
      const res = await api.post(`/tenants/${tenantId}/address-space/cascade`) as any;
      setCascadeResult(res.data);
      setShowCascadeConfirm(false);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to apply');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/address-space`, { baseCidr: editCidr });
      setShowConfirm(false);
      setEditing(false);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
        <div className="space-y-2">
          <div className="h-4 bg-gray-100 rounded w-64" />
          <div className="h-4 bg-gray-100 rounded w-56" />
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <Globe className="w-5 h-5 text-primary-600" />
          <span>Address Space</span>
        </h3>
        <button onClick={fetchData} className="text-gray-400 hover:text-gray-600">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
      )}

      {data?.isLegacy ? (
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Legacy Address Ranges</p>
                <p className="text-xs text-amber-700 mt-1">
                  This tenant uses hardcoded IP ranges from before the unified address space feature.
                  Allocate a dedicated address space to enable single-CIDR routing and avoid IP conflicts.
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y">
            <CopyField label="WireGuard VPN" value="10.10.0.0/16" />
            <CopyField label="IKEv2 VPN" value="10.11.0.0/16" />
            <CopyField label="Connector" value="10.200.0.0/24" />
            <CopyField label="E2E Peering" value="10.99.0.0/24" />
          </div>
          <button
            onClick={handleAutoAllocate}
            disabled={saving}
            className="btn-primary text-sm"
          >
            {saving ? 'Allocating...' : 'Allocate Unified Address Space'}
          </button>
        </div>
      ) : data ? (
        <div className="space-y-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-800">
              <span className="font-semibold">Unified Address Space</span> —
              all services use a single CIDR for simplified routing
              {data.autoAllocated && ' (auto-allocated)'}
            </p>
          </div>

          <div className="divide-y">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-700">Base CIDR</span>
              {editing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editCidr}
                    onChange={(e) => setEditCidr(e.target.value)}
                    className="input-base text-sm py-1 px-2 w-44 font-mono"
                    placeholder="e.g., 100.64.5.0/16"
                  />
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="text-xs btn-primary py-1 px-2"
                    disabled={saving || editCidr === data.baseCidr}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditing(false); setEditCidr(data.baseCidr); }}
                    className="text-xs btn-secondary py-1 px-2"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <code className="text-sm font-mono font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded">
                    {data.baseCidr}
                  </code>
                  <button
                    onClick={() => setEditing(true)}
                    className="text-xs text-gray-500 hover:text-primary-600 underline"
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Service Subnets</p>
            <div className="divide-y">
              <CopyField label="App VPN (WireGuard)" value={data.subnets.wg_vpn} />
              <CopyField label="App VPN (IKEv2)" value={data.subnets.ikev2} />
              <CopyField label="Connector Backbone" value={data.subnets.connector} />
              <CopyField label="E2E Peering" value={data.subnets.peering} />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => setShowCascadeConfirm(true)}
              disabled={saving}
              className="btn-primary text-sm"
            >
              {saving ? 'Applying...' : 'Apply to All Resources'}
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Rewrites IP addresses on all edges, connectors, peering tunnels, and device groups. Marks all edges for re-sync.
            </p>
          </div>

          {cascadeResult && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
              <p className="font-medium">Migration complete:</p>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>App Servers updated: {cascadeResult.updatedAppServers}</li>
                <li>IoT Gateways updated: {cascadeResult.updatedWg0Configs}</li>
                <li>Device Groups updated: {cascadeResult.updatedDeviceGroups}</li>
                <li>Connectors updated: {cascadeResult.updatedConnectors}</li>
                <li>Peering topologies updated: {cascadeResult.updatedPeerings}</li>
                <li>Edges marked dirty: {cascadeResult.dirtyEdges}</li>
              </ul>
              <p className="mt-2 text-xs">Sync edges from the dashboard to push the new configuration.</p>
            </div>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSave}
        title="Change Tenant Address Space"
        message={`WARNING: Changing the address space from ${data?.baseCidr} to ${editCidr} will:\n\n• Re-IP all edges, connectors, and VPN clients for this tenant\n• All VPN clients must re-register with new tunnel configurations\n• All edges will be re-synced with new IP ranges\n• This operation cannot be undone without manual intervention\n\nAre you sure you want to proceed?`}
        confirmText="Yes, Change Address Space"
        variant="danger"
        isLoading={saving}
      />

      <ConfirmDialog
        isOpen={showCascadeConfirm}
        onClose={() => setShowCascadeConfirm(false)}
        onConfirm={handleCascade}
        title="Apply Address Space to All Resources"
        message={`This will rewrite all IP addresses for this tenant's edges, connectors, device groups, and peering topologies to use the ${data?.baseCidr} address space.\n\n• All edges will be marked dirty and must be re-synced\n• VPN clients will need to re-register\n• Connector tunnels will be re-established\n\nProceed?`}
        confirmText="Yes, Apply Changes"
        variant="danger"
        isLoading={saving}
      />
    </div>
  );
};

export default TenantAddressSpace;
